import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  ArcElement,
  Tooltip,
} from 'chart.js';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import { Navigate } from 'react-router-dom';

import {
  exportReport,
  getApiErrorMessage,
  getReport,
  listDepartments,
} from '../../api/client';
import type { DepartmentSummary, ReportType } from '../../api/types';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import {
  getChartColors,
  barChartOptions,
  doughnutChartOptions,
  lineChartOptions,
} from '../../utils/chartTheme';
import { canExportReports, canViewReports, isSuperAdmin } from '../../utils/permissions';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
);

const REPORT_TYPES: { value: ReportType; label: string }[] = [
  { value: 'ticket_volume', label: 'Ticket Volume' },
  { value: 'sla_compliance', label: 'SLA Compliance' },
  { value: 'agent_performance', label: 'Agent Performance' },
  { value: 'channel_breakdown', label: 'Channel Breakdown' },
];

function isoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function daysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return isoDate(d);
}

export default function ReportsPage() {
  const { user } = useAuth();
  const [dateFrom, setDateFrom] = useState(daysAgo(30));
  const [dateTo, setDateTo] = useState(isoDate(new Date()));
  const [quickRange, setQuickRange] = useState<'7' | '30' | '90' | 'custom'>('30');
  const [reportType, setReportType] = useState<ReportType>('ticket_volume');
  const [departmentId, setDepartmentId] = useState('');
  const [departments, setDepartments] = useState<DepartmentSummary[]>([]);
  const [report, setReport] = useState<Record<string, unknown> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(false);
  const { resolvedTheme } = useTheme();
  const themeKey = resolvedTheme;
  const chartColors = useMemo(() => getChartColors(), [themeKey]);

  useEffect(() => {
    if (user && isSuperAdmin(user.role)) {
      void listDepartments().then(setDepartments).catch(() => undefined);
    } else if (user) {
      setDepartments(user.departmentMemberships.map((m) => m.department));
    }
  }, [user]);

  const loadReport = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getReport(reportType, {
        dateFrom,
        dateTo,
        departmentId: departmentId || undefined,
      });
      setReport(data);
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [reportType, dateFrom, dateTo, departmentId]);

  useEffect(() => {
    void loadReport();
  }, [loadReport]);

  function applyQuickRange(range: '7' | '30' | '90' | 'custom') {
    setQuickRange(range);
    if (range !== 'custom') {
      setDateFrom(daysAgo(Number(range)));
      setDateTo(isoDate(new Date()));
    }
  }

  async function handleExport() {
    if (!user || !canExportReports(user)) return;
    setExporting(true);
    try {
      const blob = await exportReport({
        reportType,
        dateFrom,
        dateTo,
        departmentId: departmentId || undefined,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${reportType}-${dateFrom}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setExporting(false);
    }
  }

  const lineOpts = useMemo(() => lineChartOptions(), [themeKey]);
  const donutOpts = useMemo(() => doughnutChartOptions(), [themeKey]);
  const barOpts = useMemo(() => barChartOptions(), [themeKey]);

  if (!user || !canViewReports(user)) {
    return <Navigate to="/" replace />;
  }

  const canExport = canExportReports(user);

  return (
    <>
      <div className="page-header">
        <div>
          <h1>Reports</h1>
          <p className="page-subtitle">Analytics and exports for ticket operations</p>
        </div>
        <button
          type="button"
          className="btn btn-primary"
          disabled={!canExport || exporting}
          onClick={() => void handleExport()}
        >
          {exporting ? 'Exporting…' : 'Export CSV'}
        </button>
      </div>

      <div className="reports-filter-bar card card-body">
        <div className="form-row">
          <div className="form-group">
            <label>From</label>
            <input
              type="date"
              className="form-control"
              value={dateFrom}
              onChange={(e) => {
                setDateFrom(e.target.value);
                setQuickRange('custom');
              }}
            />
          </div>
          <div className="form-group">
            <label>To</label>
            <input
              type="date"
              className="form-control"
              value={dateTo}
              onChange={(e) => {
                setDateTo(e.target.value);
                setQuickRange('custom');
              }}
            />
          </div>
          <div className="form-group">
            <label>Quick range</label>
            <div className="quick-ranges">
              {(['7', '30', '90'] as const).map((n) => (
                <button
                  key={n}
                  type="button"
                  className={`btn btn-secondary btn-sm ${quickRange === n ? 'active' : ''}`}
                  onClick={() => applyQuickRange(n)}
                >
                  Last {n} days
                </button>
              ))}
              <button
                type="button"
                className={`btn btn-secondary btn-sm ${quickRange === 'custom' ? 'active' : ''}`}
                onClick={() => setQuickRange('custom')}
              >
                Custom
              </button>
            </div>
          </div>
          {departments.length > 0 && (
            <div className="form-group">
              <label>Department</label>
              <select
                className="form-control"
                value={departmentId}
                onChange={(e) => setDepartmentId(e.target.value)}
              >
                {isSuperAdmin(user.role) && <option value="">All departments</option>}
                {departments.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="form-group">
            <label>Report type</label>
            <select
              className="form-control"
              value={reportType}
              onChange={(e) => setReportType(e.target.value as ReportType)}
            >
              {REPORT_TYPES.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {loading && <div className="loading">Loading report…</div>}

      {!loading && report && (
        <div className="report-content">
          {reportType === 'ticket_volume' && (
            <>
              <div className="card card-body chart-card">
                <h3>Created vs resolved</h3>
                <div className="chart-box">
                  <Line
                    key={`rv-${themeKey}`}
                    options={lineOpts}
                    data={{
                      labels: ((report.trend as Array<{ date: string }>) ?? []).map((d) =>
                        d.date.slice(5),
                      ),
                      datasets: [
                        {
                          label: 'Created',
                          data: ((report.trend as Array<{ created: number }>) ?? []).map(
                            (d) => d.created,
                          ),
                          borderColor: chartColors.primary,
                          tension: 0.3,
                        },
                        {
                          label: 'Resolved',
                          data: ((report.trend as Array<{ resolved: number }>) ?? []).map(
                            (d) => d.resolved,
                          ),
                          borderColor: chartColors.success,
                          tension: 0.3,
                        },
                      ],
                    }}
                  />
                </div>
              </div>
              <div className="card card-body">
                <h3>Summary</h3>
                <div className="table-wrap">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Department</th>
                        <th>Created</th>
                        <th>Resolved</th>
                        <th>Open at end</th>
                        <th>Avg resolution</th>
                      </tr>
                    </thead>
                    <tbody>
                      {((report.summary as Array<Record<string, unknown>>) ?? []).map((row) => (
                        <tr key={String(row.departmentId)}>
                          <td>{String(row.department)}</td>
                          <td>{Number(row.created)}</td>
                          <td>{Number(row.resolved)}</td>
                          <td>{Number(row.openAtEnd)}</td>
                          <td>{Number(row.avgResolutionHours)}h</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          {reportType === 'sla_compliance' && (
            <>
              <div className="card card-body chart-card">
                <h3>Compliance trend</h3>
                <div className="chart-box">
                  <Line
                    key={`sla-${themeKey}`}
                    options={lineOpts}
                    data={{
                      labels: ((report.trend as Array<{ week: string }>) ?? []).map((w) => w.week),
                      datasets: [
                        {
                          label: 'Compliance %',
                          data: ((report.trend as Array<{ compliancePct: number }>) ?? []).map(
                            (w) => w.compliancePct,
                          ),
                          borderColor: chartColors.warning,
                          tension: 0.3,
                        },
                      ],
                    }}
                  />
                </div>
              </div>
              <div className="card card-body">
                <h3>By department & priority</h3>
                <div className="table-wrap">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Department</th>
                        <th>Priority</th>
                        <th>Total</th>
                        <th>Met SLA</th>
                        <th>Breached</th>
                        <th>Compliance %</th>
                      </tr>
                    </thead>
                    <tbody>
                      {((report.table as Array<Record<string, unknown>>) ?? []).map((row, i) => (
                        <tr key={i}>
                          <td>{String(row.department)}</td>
                          <td>{String(row.priority)}</td>
                          <td>{Number(row.total)}</td>
                          <td>{Number(row.metSla)}</td>
                          <td>{Number(row.breached)}</td>
                          <td>{Number(row.compliancePct)}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          {reportType === 'agent_performance' && (
            <>
              <div className="card card-body chart-card">
                <h3>Top agents by resolved</h3>
                <div className="chart-box">
                  <Bar
                    key={`ap-${themeKey}`}
                    options={barOpts}
                    data={{
                      labels: ((report.chart as Array<{ agent: string }>) ?? []).map((r) => r.agent),
                      datasets: [
                        {
                          label: 'Resolved',
                          data: ((report.chart as Array<{ resolved: number }>) ?? []).map(
                            (r) => r.resolved,
                          ),
                          backgroundColor: chartColors.success,
                        },
                      ],
                    }}
                  />
                </div>
              </div>
              <div className="card card-body">
                <h3>Agent performance</h3>
                <div className="table-wrap">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Agent</th>
                        <th>Department</th>
                        <th>Team</th>
                        <th>Open</th>
                        <th>Resolved</th>
                        <th>Avg first response</th>
                        <th>Avg resolution</th>
                        <th>SLA %</th>
                        <th>Escalation rate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {((report.table as Array<Record<string, unknown>>) ?? []).map((row) => (
                        <tr key={String(row.agentId)}>
                          <td>{String(row.agent)}</td>
                          <td>{String(row.department)}</td>
                          <td>{String(row.team)}</td>
                          <td>{Number(row.open)}</td>
                          <td>{Number(row.resolved)}</td>
                          <td>{String(row.avgFirstResponse)}</td>
                          <td>{String(row.avgResolution)}</td>
                          <td>{Number(row.slaPct)}%</td>
                          <td>{Number(row.escalationRate)}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          {reportType === 'channel_breakdown' && (
            <>
              <div className="card card-body chart-card">
                <h3>Tickets by channel</h3>
                <div className="chart-box chart-box-sm">
                  <Doughnut
                    key={`ch-${themeKey}`}
                    options={donutOpts}
                    data={{
                      labels: ((report.donut as Array<{ channel: string }>) ?? []).map(
                        (d) => d.channel,
                      ),
                      datasets: [
                        {
                          data: ((report.donut as Array<{ count: number }>) ?? []).map(
                            (d) => d.count,
                          ),
                          backgroundColor: chartColors.channels,
                        },
                      ],
                    }}
                  />
                </div>
              </div>
              <div className="card card-body">
                <h3>Channel breakdown</h3>
                <div className="table-wrap">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Channel</th>
                        <th>Count</th>
                        <th>%</th>
                        <th>Avg resolution</th>
                        <th>SLA compliance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {((report.table as Array<Record<string, unknown>>) ?? []).map((row) => (
                        <tr key={String(row.channel)}>
                          <td>{String(row.channel)}</td>
                          <td>{Number(row.count)}</td>
                          <td>{Number(row.pct)}%</td>
                          <td>{Number(row.avgResolutionHours)}h</td>
                          <td>{Number(row.slaCompliancePct)}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}
